import React from 'react';
import styled from '@emotion/styled';
import { css, Theme } from '@emotion/react';
import { rem } from 'polished';
import { H4 } from '../../primitives/Typography';
import Link from '../../primitives/Link';
import Spacer from '../../helpers/Spacer';
import {
  colors,
  mixins,
  text,
  spaces,
  fontWeights,
  animations,
} from '../../core/styles/theme/default';
import ImageWithCaption from '../../components/Image/ImageWithCaption';
import { IconInterface, ImageInterface } from '~/types';

const StyledCard = styled.div(
  ({ isSmall }: { isSmall?: boolean }) => css`
    position: relative;
    color: ${colors.white};
    height: 100%;
    display: flex;
    flex-direction: column;
    cursor: pointer;

    ${isSmall &&
    css`
      flex-flow: row-reverse nowrap;
      padding-bottom: ${rem(10)};
      border-bottom: solid ${rem(1)} #d5d5d5;
    `};
  `,
);

const StyledContent = styled.div(
  ({
    isSmall,
    variant,
    theme,
  }: {
    isSmall?: boolean;
    variant?: string;
    theme?: Theme;
  }) => css`
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: ${rem(spaces.delta)};
    border: ${rem(1)} solid ${colors.grue300};
    border-radius: ${rem(2)};
    background: ${colors.white};

    ${mixins.MQ('medium')} {
      ${variant === 'related' &&
      css`
        padding: ${rem(spaces.epsilon)};
      `};
    }

    ${mixins.MQ('ultra')} {
      padding: ${rem(spaces.epsilon)};
    }

    ${isSmall &&
    css`
      overflow: hidden;
      flex: 1 1 auto;
      padding: 0 ${rem(spaces.delta)} 0 0;
      border: 0;
    `};

    ${variant === 'backgroundImage' &&
    css`
      background: none;
      border: none;
      z-index: 1;
    `}

    ${theme?.stylesExtension?.teaserCard?.default?.content};
    ${variant === 'news' && theme?.stylesExtension?.teaserCard?.news?.content};
  `,
);

const StyledTitle = styled(H4)(
  ({ isSmall, variant }: { isSmall?: boolean; variant?: string }) => css`
    margin-bottom: ${rem(spaces.alpha)};
    font-size: ${rem(20)};
    line-height: ${rem(28)};

    ${variant === 'news' &&
    css`
      line-height: ${rem(32)};
    `};

    ${mixins.MQ('medium')} {
      margin-bottom: ${rem(spaces.beta)};
      font-size: ${rem(24)};
      ${variant === 'news' &&
      css`
        font-size: ${rem(20)};
      `};

      line-height: ${rem(30)};
      ${variant === 'news' &&
      css`
        line-height: ${rem(29)};
      `};
    }

    ${isSmall &&
    css`
      min-width: 0;
      margin-bottom: 0;
      font-size: ${rem(16)};
      line-height: ${rem(25)};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      ${mixins.MQ('medium')} {
        margin-bottom: 0;
        font-size: ${rem(16)};
        line-height: ${rem(25)};
      }
    `};

    ${variant === 'backgroundImage' &&
    css`
      color: ${colors.white};
    `}
  `,
);

const StyledCardMain = styled.div(
  ({ isSmall, variant }: { isSmall?: boolean; variant?: string }) => css`
    flex: 1;
    margin-bottom: ${rem(spaces.eta)};
    font-size: ${rem(18)};
    ${variant === 'news' &&
    css`
      font-size: ${rem(16)};
    `};

    color: ${colors.grey700};

    line-height: ${rem(26)};
    ${variant === 'news' &&
    css`
      line-height: ${rem(25)};
    `};

    ${mixins.MQ('medium')} {
      margin-bottom: ${rem(spaces.epsilon)};
    }

    ${mixins.MQ('ultra')} {
      margin-bottom: ${rem(spaces.eta)};
      line-height: ${rem(28)};
      ${variant === 'news' &&
      css`
        line-height: ${rem(25)};
      `};
    }

    ${isSmall &&
    css`
      margin-bottom: ${rem(spaces.beta)} !important;
      font-size: ${rem(16)} !important;
      line-height: ${rem(22)} !important;

      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    `};

    ${variant === 'backgroundImage' &&
    css`
      color: ${colors.white};
    `}
  `,
);

const StyledLink = styled(Link)(
  ({ color, theme }: { color?: string; theme?: Theme }) => css`
    i {
      padding-right: ${rem(23)};
      font-size: ${rem(11)};
    }

    color: ${color || colors.nightBlue500};
    padding: ${rem(10)} 0;

    .label {
      position: relative;
      font-weight: 500;

      &::after {
        content: '';
        border-bottom: ${rem(1)} solid currentColor;
        width: 100%;
        position: absolute;
        left: 0;
        bottom: ${rem(2)};
      }
    }

    &:hover {
      color: ${color || colors.nightBlue500};

      .label::after {
        content: none;
      }
    }

    .m-richtext & {
      text-decoration: none;
    }

    ${theme?.stylesExtension?.teaserCard?.default?.link};
  `,
);

const StyledImage = styled.div(
  ({ isSmall, variant }: { isSmall?: boolean; variant?: string }) => css`
    background: grey;
    position: relative;

    ${isSmall &&
    css`
      display: block;
      flex: 0 0 ${rem(72)};
      width: ${rem(72)};
      height: ${rem(72)};
    `};

    figure,
    picture,
    .gatsby-image-wrapper {
      width: 100%;
      height: 100%;
    }

    figure,
    picture {
      position: absolute;
      top: 0;
      left: 0;
    }

    ${mixins.MQ('large')} {
      picture {
        opacity: 0.7;
        transition: opacity ${animations.default},
          transform ${animations.default};
        transform: scale(1.07);
      }

      .item:hover & {
        picture {
          opacity: 0.5;
          transform: scale(1);
        }
      }
    }

    ${variant === 'backgroundImage' &&
    css`
      background: rgba(0, 0, 0, 0.8);
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    `}
  `,
);

const StyledTop = styled.div(
  ({ isSmall }: { isSmall?: boolean }) => css`
    flex: 1;

    ${isSmall &&
    css`
      flex: 0 1 auto;
      display: flex;
      flex-flow: column-reverse nowrap;
    `};
  `,
);

const StyledBottom = styled.div(
  ({ variant }: { variant?: string }) => css`
    ${variant === 'backgroundImage' &&
    css`
      a .label {
        color: ${colors.white};
      }
    `}
  `,
);

const StyledPlaceholder = styled.div(
  ({ variant }: { variant?: string }) => css`
    background: linear-gradient(
      90deg,
      rgba(37, 55, 100, 0.9) 0%,
      rgba(37, 55, 100, 0.6) 100%
    );
    padding-bottom: 50%;

    ${variant === 'backgroundImage' &&
    css`
      padding-bottom: 0;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 1;
      background: linear-gradient(
        90deg,
        rgba(13, 40, 68, 0.6) 0%,
        rgba(18, 56, 95, 0.3) 100%
      );
    `}
  `,
);

const StyledDate = styled.time`
  display: block;
  ${mixins.responsiveStyles({
    'padding-bottom': { zero: rem(spaces.gamma) },
  })}

  ${text.p4};
  color: ${colors.nightBlue700};

  ${({ theme }) => theme?.stylesExtension?.teaserCard?.default?.date}
`;

const StyledPreTitle = styled.span`
  display: block;
  ${mixins.responsiveStyles({
    'padding-bottom': { zero: rem(spaces.gamma) },
  })}

  ${text.p4};
  color: ${colors.nightBlue700};
  font-weight: ${fontWeights.regular};
  text-transform: uppercase;
`;

const StyledLogo = styled(ImageWithCaption)`
  width: ${rem(92)};
`;

const TeaserCard = ({ variant, isSmall, ...props }: TeaserCardProps) => {
  const {
    date,
    pretitle,
    title,
    teaser,
    teaserImage,
    logo,
    href,
    linkText,
    linkTitle = linkText,
    linkTarget = '_self',
    linkIconBefore = 'AnimatedIconArrowRight',
    linkIconAfter,
    linkColor,
    trackClick,
  } = props.data;

  if (variant === 'backgroundImage' && !teaserImage) {
    variant = undefined;
  }

  return (
    <>
      <StyledCard
        className={'item'}
        isSmall={isSmall}
        onClick={() => {
          trackClick && trackClick();

          if (linkTarget === '_self') {
            window.location.href = href;
          } else {
            window.open(href, '_blank');
          }
        }}
      >
        {teaserImage && (
          <StyledImage isSmall={isSmall} variant={variant}>
            {!isSmall && <StyledPlaceholder variant={variant} />}
            <ImageWithCaption data={{ ...teaserImage, disableLink: true }} />
          </StyledImage>
        )}
        <StyledContent variant={variant} isSmall={isSmall}>
          {logo && (
            <>
              <StyledLogo data={{ image: logo, disableLink: true }} />
              <Spacer space="epsilon" />
            </>
          )}
          <StyledTop isSmall={isSmall}>
            {date ? (
              <StyledDate dateTime={date.split('T')[0]}>
                {date.split('T')[0].split('-').reverse().join('/')}
              </StyledDate>
            ) : null}
            {pretitle ? <StyledPreTitle>{pretitle}</StyledPreTitle> : null}
            {title ? (
              <StyledTitle variant={variant} isSmall={isSmall}>
                {title}
              </StyledTitle>
            ) : null}
            <StyledCardMain variant={variant} isSmall={isSmall}>
              {teaser}
            </StyledCardMain>
          </StyledTop>
          {!isSmall ? (
            <StyledBottom variant={variant}>
              <StyledLink
                linkText={linkText}
                link={{
                  href,
                  title: linkTitle,
                  target: linkTarget,
                }}
                iconBefore={linkIconBefore}
                iconAfter={linkIconAfter}
                className="animate"
                color={linkColor}
              />
            </StyledBottom>
          ) : null}
        </StyledContent>
      </StyledCard>
    </>
  );
};

export type TeaserCardProps = {
  data: {
    contentful_id?: string;
    pretitle?: string;
    title: string;
    teaser?: string;
    teaserImage?: ImageInterface;
    logo?: ImageInterface['image'];
    belongsToOverview?: {
      slug: string;
    };
    slug?: string;
    node_locale?: string;
    tag?: string;
    folder?: string;
    date?: string;
    linkText: string;
    linkTitle?: string;
    linkTarget?: string;
    linkIconBefore?: IconInterface | null;
    linkIconAfter?: IconInterface | null;
    linkColor?: string;
    href: string;
    trackClick?: () => void;
  };
  tag?: string;
  variant?: string;
  isSmall?: boolean;
};

export default TeaserCard;
